import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Rating,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Divider,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Badge,
  Tooltip,
  Collapse,
  ImageList,
  ImageListItem,
  Pagination,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { ShoppingBag } from '@mui/icons-material';
import { orderService } from '../../services/orderService';
import lotRequestService from '../../services/lotRequestService';
import {
  Star,
  StarBorder,
  ThumbUp,
  ThumbDown,
  Reply,
  Report,
  Verified,
  PhotoCamera,
  VideoCall,
  MoreVert,
  FilterList,
  Sort,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Warning,
  Block,
  Edit,
  Delete,
  Flag,
  Share,
  Bookmark,
  BookmarkBorder,
  ExpandMore,
  ExpandLess,
  Send,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  fetchProductReviewsPublic,
  fetchSellerReviewsPublic,
  fetchProductReviewAnalytics,
  fetchSellerReviewAnalytics,
  createReview,
  updateReview,
  deleteReview,
  markReviewHelpful,
  flagReview,
  setReviewForm,
  resetReviewForm,
  setFilters,
  setSelectedReview,
  uploadReviewImages,
} from '../../store/slices/reviewSlice';

const ReviewSystem = ({ productId, sellerId, showAnalytics = false }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const {
    productReviews,
    sellerReviews,
    productAnalytics,
    sellerAnalytics,
    pagination,
    loading,
    submitting,
    uploading,
    error,
    reviewForm,
    selectedReview,
    filters,
  } = useSelector((state) => state.reviews);
  
  const [activeTab, setActiveTab] = useState(0);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedReviews, setExpandedReviews] = useState(new Set());
  const [replyText, setReplyText] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [purchaseCheckDone, setPurchaseCheckDone] = useState(false);
  
  // Refs to track loading state and prevent infinite loops
  const initialLoadRef = useRef(false);
  const currentProductIdRef = useRef(null);
  const currentSellerIdRef = useRef(null);
  const isLoadingRef = useRef(false);
  const lastParamsRef = useRef(null);
  
  // Extract primitive values from Redux state to avoid object reference issues
  const currentPage = pagination?.page ?? 0;
  const currentSize = pagination?.size ?? 10;
  const currentSortBy = filters?.sortBy ?? 'createdAt';
  const currentSortDir = filters?.sortDir ?? 'desc';
  
  // Memoize params to detect actual changes
  const currentParams = useMemo(() => ({
    page: currentPage,
    size: currentSize,
    sortBy: currentSortBy,
    sortDir: currentSortDir
  }), [currentPage, currentSize, currentSortBy, currentSortDir]);
  
  // Get current reviews based on context
  const currentReviews = productId ? (productReviews[productId] || []) : sellerReviews;
  const currentAnalytics = productId ? productAnalytics[productId] : sellerAnalytics[sellerId];
  
  // Debug logging
  console.log('ReviewSystem render - Reviews count:', currentReviews?.length || 0, 'Loading:', loading);

  // Initial load: Only run once when productId/sellerId changes
  // This effect should ONLY depend on productId/sellerId, not on filters/pagination
  useEffect(() => {
    // Reset initial load flag if productId or sellerId changed
    if (currentProductIdRef.current !== productId || currentSellerIdRef.current !== sellerId) {
      initialLoadRef.current = false;
      currentProductIdRef.current = productId;
      currentSellerIdRef.current = sellerId;
      lastParamsRef.current = null;
    }
    
    // Skip if already loading or no productId/sellerId
    if (isLoadingRef.current || (!productId && !sellerId)) {
      return;
    }
    
    // Only run initial load once per productId/sellerId
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      isLoadingRef.current = true;
      
      console.log('ReviewSystem: Initial load for productId:', productId, 'sellerId:', sellerId);
      
      // Use default params for initial load - filters/pagination changes are handled separately
      // Note: ProductDetailPage may have already fetched with size=1 for analytics,
      // but we need size=10 for the reviews list, so this fetch is still needed
      const params = { 
        page: 0, // Always start with page 0 on initial load
        size: 10, // Default size for reviews list
        sortBy: 'createdAt',
        sortDir: 'desc'
      };
      
      if (productId) {
        dispatch(fetchProductReviewsPublic({ productId, params }))
          .then((result) => {
            console.log('Reviews loaded successfully:', result.payload?.reviews?.length || 0, 'reviews');
            isLoadingRef.current = false;
            lastParamsRef.current = JSON.stringify(params);
            // Analytics are already included in the reviews response, no need for separate call
          })
          .catch((error) => {
            console.error('Error loading reviews:', error);
            isLoadingRef.current = false;
          });
        
        // Note: Analytics are already included in fetchProductReviewsPublic response
        // No need for separate fetchProductReviewAnalytics call
      } else if (sellerId) {
        dispatch(fetchSellerReviewsPublic({ sellerId, params }))
          .then(() => {
            isLoadingRef.current = false;
            lastParamsRef.current = JSON.stringify(params);
          })
          .catch((error) => {
            console.error('Error loading seller reviews:', error);
            isLoadingRef.current = false;
          });
        
        // Note: Seller analytics might need separate call if not included in reviews response
        // But we should check if they're already included first
      }
    }
  }, [dispatch, productId, sellerId]); // Removed showAnalytics and filter dependencies to prevent re-renders

  // Handle pagination and filter changes separately (only after initial load)
  useEffect(() => {
    // Skip if initial load hasn't happened yet or currently loading
    if (!initialLoadRef.current || isLoadingRef.current || (!productId && !sellerId)) {
      return;
    }
    
    // Create params string for comparison
    const paramsString = JSON.stringify(currentParams);
    
    // Only reload if params actually changed (not just object reference)
    if (lastParamsRef.current === paramsString) {
      return;
    }
    
    // Update last params before making request
    lastParamsRef.current = paramsString;
    isLoadingRef.current = true;
    
    console.log('ReviewSystem: Reloading with new params:', currentParams);
    
    if (productId) {
      dispatch(fetchProductReviewsPublic({ productId, params: currentParams }))
        .then(() => {
          isLoadingRef.current = false;
        })
        .catch((error) => {
          console.error('Error reloading reviews:', error);
          isLoadingRef.current = false;
        });
    } else if (sellerId) {
      dispatch(fetchSellerReviewsPublic({ sellerId, params: currentParams }))
        .then(() => {
          isLoadingRef.current = false;
        })
        .catch((error) => {
          console.error('Error reloading seller reviews:', error);
          isLoadingRef.current = false;
        });
    }
  }, [dispatch, productId, sellerId, currentParams]);

  // Check if the logged-in customer has purchased this product via orders or lot requests
  useEffect(() => {
    if (!user || user.role !== 'CUSTOMER' || !productId) {
      setPurchaseCheckDone(true);
      return;
    }

    const checkPurchase = async () => {
      try {
        const [ordersData, lotData] = await Promise.allSettled([
          orderService.getCustomerOrders(user.id, { page: 0, size: 10 }),
          lotRequestService.getLotRequestsByCustomer(user.id, 0, 10),
        ]);

        let purchased = false;

        if (ordersData.status === 'fulfilled') {
          const orders = ordersData.value?.orders || ordersData.value?.content || ordersData.value || [];
          purchased = orders.some((order) =>
            (order.items || []).some((item) => item.productId === productId)
          );
        }

        if (!purchased && lotData.status === 'fulfilled') {
          const lots = lotData.value?.content || lotData.value?.lotRequests || lotData.value || [];
          purchased = lots.some((lot) => lot.productId === productId);
        }

        setHasPurchased(purchased);
      } catch {
        setHasPurchased(false);
      } finally {
        setPurchaseCheckDone(true);
      }
    };

    checkPurchase();
  }, [user, productId]);

  // Handle form changes
  const handleFormChange = (field, value) => {
    dispatch(setReviewForm({ [field]: value }));
  };

  // Handle image upload
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      try {
        await dispatch(uploadReviewImages(files)).unwrap();
        toast.success('Images uploaded successfully!');
      } catch (error) {
        toast.error('Failed to upload images: ' + error);
      }
    }
  };

  // Handle review submission
  const handleSubmitReview = async () => {
    if (reviewForm.rating === 0) {
      toast.error('Please provide a rating');
      return;
    }
    
    if (!reviewForm.comment.trim()) {
      toast.error('Please write a review comment');
      return;
    }

    if (!user) {
      toast.error('Please login to submit a review');
      return;
    }
    
    // Prepare review data with required backend fields
    const reviewData = {
      rating: reviewForm.rating,
      title: reviewForm.title || 'Review',
      comment: reviewForm.comment,
      customerId: user?.id, // Get user ID from logged-in user
      orderId: null, // Set to null for now, can be updated later if needed
      reviewType: productId ? 'PRODUCT' : 'SELLER',
      ...(productId && { productId }),
      ...(sellerId && { sellerId })
    };
    
    try {
      await dispatch(createReview(reviewData)).unwrap();
      setReviewDialogOpen(false);
      dispatch(resetReviewForm());
      toast.success('Review submitted and published successfully!');
      
      // Refresh reviews
      const refreshParams = { page: 0, size: currentSize, sortBy: currentSortBy, sortDir: currentSortDir };
      lastParamsRef.current = JSON.stringify(refreshParams);
      isLoadingRef.current = true;
      
      if (productId) {
        dispatch(fetchProductReviewsPublic({ productId, params: refreshParams }))
          .then(() => {
            isLoadingRef.current = false;
          })
          .catch(() => {
            isLoadingRef.current = false;
          });
      } else if (sellerId) {
        dispatch(fetchSellerReviewsPublic({ sellerId, params: refreshParams }))
          .then(() => {
            isLoadingRef.current = false;
          })
          .catch(() => {
            isLoadingRef.current = false;
          });
      }
    } catch (error) {
      toast.error('Failed to submit review: ' + error);
    }
  };

  // Handle helpful marking
  const handleHelpful = async (reviewId, isHelpful) => {
    if (!user) {
      toast.error('Please login to mark reviews as helpful');
      return;
    }

    try {
      await dispatch(markReviewHelpful({ reviewId, helpful: isHelpful })).unwrap();
      toast.success(isHelpful ? 'Marked as helpful' : 'Feedback recorded');
    } catch (error) {
      toast.error('Failed to record feedback: ' + error);
    }
  };

  // Handle report
  const handleReport = async (reviewId, reason = 'inappropriate') => {
    if (!user) {
      toast.error('Please login to report reviews');
      return;
    }

    try {
      await dispatch(flagReview({ reviewId, reason })).unwrap();
      toast.success('Review reported. Thank you for your feedback.');
    } catch (error) {
      toast.error('Failed to report review: ' + error);
    }
  };

  // Handle delete review
  const handleDeleteReview = async (reviewId) => {
    if (!user) {
      toast.error('Please login to delete reviews');
      return;
    }

    try {
      await dispatch(deleteReview(reviewId)).unwrap();
      toast.success('Review deleted successfully');
    } catch (error) {
      toast.error('Failed to delete review: ' + error);
    }
  };

  // Handle pagination
  const handlePageChange = (event, newPage) => {
    const params = { 
      page: newPage - 1, 
      size: currentSize,
      sortBy: currentSortBy,
      sortDir: currentSortDir 
    };
    
    // Update lastParamsRef to prevent useEffect from triggering
    lastParamsRef.current = JSON.stringify(params);
    isLoadingRef.current = true;
    
    if (productId) {
      dispatch(fetchProductReviewsPublic({ productId, params }))
        .then(() => {
          isLoadingRef.current = false;
        })
        .catch(() => {
          isLoadingRef.current = false;
        });
    } else if (sellerId) {
      dispatch(fetchSellerReviewsPublic({ sellerId, params }))
        .then(() => {
          isLoadingRef.current = false;
        })
        .catch(() => {
          isLoadingRef.current = false;
        });
    }
  };

  // Handle sort change
  const handleSortChange = (sortBy, sortDir = 'desc') => {
    dispatch(setFilters({ sortBy, sortDir }));
    
    const params = { 
      page: 0, 
      size: currentSize,
      sortBy,
      sortDir 
    };
    
    // Update lastParamsRef to prevent useEffect from triggering
    lastParamsRef.current = JSON.stringify(params);
    isLoadingRef.current = true;
    
    if (productId) {
      dispatch(fetchProductReviewsPublic({ productId, params }))
        .then(() => {
          isLoadingRef.current = false;
        })
        .catch(() => {
          isLoadingRef.current = false;
        });
    } else if (sellerId) {
      dispatch(fetchSellerReviewsPublic({ sellerId, params }))
        .then(() => {
          isLoadingRef.current = false;
        })
        .catch(() => {
          isLoadingRef.current = false;
        });
    }
  };

  // Toggle expanded review
  const toggleExpandedReview = (reviewId) => {
    const newExpanded = new Set(expandedReviews);
    if (newExpanded.has(reviewId)) {
      newExpanded.delete(reviewId);
    } else {
      newExpanded.add(reviewId);
    }
    setExpandedReviews(newExpanded);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Check if user can review — customers must have purchased the product
  const canUserReview = () => {
    if (!user) return false;
    if (productId && user.role === 'CUSTOMER') {
      return hasPurchased;
    }
    // Sellers are not gated by purchase for seller-level reviews
    return user.role === 'CUSTOMER';
  };

  const renderReviewStats = () => {
    if (!showAnalytics || !currentAnalytics) return null;

    // Normalize averageRating to a 1-5 scale — backend may return on a 0-10 scale
    const rawRating = currentAnalytics.averageRating || 0;
    const displayRating = rawRating > 5 ? rawRating / 2 : rawRating;

    return (
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Review Statistics" />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box textAlign="center">
                <Typography variant="h3" color="primary">
                  {displayRating.toFixed(1)}
                </Typography>
                <Rating value={displayRating} readOnly precision={0.1} />
                <Typography variant="body2" color="text.secondary">
                  Based on {currentAnalytics.totalReviews || 0} reviews
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              {currentAnalytics.ratingDistribution && Object.entries(currentAnalytics.ratingDistribution)
                .sort(([a], [b]) => b - a)
                .map(([rating, count]) => (
                  <Box key={rating} display="flex" alignItems="center" mb={1}>
                    <Typography variant="body2" sx={{ minWidth: 20 }}>
                      {rating}
                    </Typography>
                    <Star fontSize="small" sx={{ mx: 1 }} />
                    <LinearProgress
                      variant="determinate"
                      value={(count / (currentAnalytics.totalReviews || 1)) * 100}
                      sx={{ flexGrow: 1, mx: 2 }}
                    />
                    <Typography variant="body2" sx={{ minWidth: 30 }}>
                      {count}
                    </Typography>
                  </Box>
                ))}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderReviewForm = () => (
    <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Write a Review</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography component="legend" gutterBottom>
            Overall Rating *
          </Typography>
          <Rating
            value={reviewForm.rating}
            onChange={(event, newValue) => handleFormChange('rating', newValue)}
            size="large"
            sx={{ mb: 3 }}
          />
          
          <TextField
            fullWidth
            label="Review Title"
            value={reviewForm.title}
            onChange={(e) => handleFormChange('title', e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Your Review *"
            value={reviewForm.comment}
            onChange={(e) => handleFormChange('comment', e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Pros"
                value={reviewForm.pros}
                onChange={(e) => handleFormChange('pros', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Cons"
                value={reviewForm.cons}
                onChange={(e) => handleFormChange('cons', e.target.value)}
              />
            </Grid>
          </Grid>
          
          <FormControlLabel
            control={
              <Switch
                checked={reviewForm.wouldRecommend}
                onChange={(e) => handleFormChange('wouldRecommend', e.target.checked)}
              />
            }
            label="Would you recommend this?"
            sx={{ mb: 2 }}
          />
          
          <Box sx={{ mb: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="review-images"
              multiple
              type="file"
              onChange={handleImageUpload}
            />
            <label htmlFor="review-images">
              <Button
                variant="outlined"
                component="span"
                startIcon={<PhotoCamera />}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Add Photos'}
              </Button>
            </label>
            {uploading && <CircularProgress size={20} sx={{ ml: 2 }} />}
          </Box>
          
          {reviewForm.images.length > 0 && (
            <ImageList cols={3} rowHeight={100} sx={{ mb: 2 }}>
              {reviewForm.images.map((image, index) => (
                <ImageListItem key={index}>
                  <img src={image} alt={`Review ${index + 1}`} loading="lazy" />
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
        <Button 
          onClick={handleSubmitReview} 
          variant="contained"
          disabled={submitting || reviewForm.rating === 0 || !reviewForm.comment.trim()}
          startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
        >
          {submitting ? 'Submitting...' : 'Submit Review'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderReviewItem = (review) => {
    const isExpanded = expandedReviews.has(review.id);
    const canEdit = user && user.id === review.userId;
    const canDelete = user && (user.id === review.userId || user.role === 'ADMIN');

    return (
      <motion.div
        key={review.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Box display="flex" alignItems="center">
                <Avatar src={review.userAvatar} sx={{ mr: 2 }}>
                  {review.userName?.charAt(0)}
                </Avatar>
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {review.userName}
                    </Typography>
                    {review.verified && (
                      <Tooltip title="Verified Purchase">
                        <Verified color="primary" fontSize="small" />
                      </Tooltip>
                    )}
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Rating value={review.rating} readOnly size="small" />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(review.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              <Box>
                <IconButton
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  size="small"
                >
                  <MoreVert />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                >
                  {canEdit && (
                    <MenuItem onClick={() => {
                      // Handle edit
                      setAnchorEl(null);
                    }}>
                      <Edit fontSize="small" sx={{ mr: 1 }} />
                      Edit
                    </MenuItem>
                  )}
                  {canDelete && (
                    <MenuItem onClick={() => {
                      handleDeleteReview(review.id);
                      setAnchorEl(null);
                    }}>
                      <Delete fontSize="small" sx={{ mr: 1 }} />
                      Delete
                    </MenuItem>
                  )}
                  <MenuItem onClick={() => {
                    handleReport(review.id);
                    setAnchorEl(null);
                  }}>
                    <Report fontSize="small" sx={{ mr: 1 }} />
                    Report
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
            
            {review.title && (
              <Typography variant="h6" gutterBottom>
                {review.title}
              </Typography>
            )}
            
            <Typography variant="body1" paragraph>
              {isExpanded ? review.comment : `${review.comment?.substring(0, 200)}${review.comment?.length > 200 ? '...' : ''}`}
            </Typography>
            
            {review.comment?.length > 200 && (
              <Button
                size="small"
                onClick={() => toggleExpandedReview(review.id)}
                endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
              >
                {isExpanded ? 'Show Less' : 'Show More'}
              </Button>
            )}
            
            {(review.pros || review.cons) && (
              <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                {review.pros && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="success.main">
                      Pros:
                    </Typography>
                    <Typography variant="body2">
                      {review.pros}
                    </Typography>
                  </Grid>
                )}
                {review.cons && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="error.main">
                      Cons:
                    </Typography>
                    <Typography variant="body2">
                      {review.cons}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}
            
            {review.images && review.images.length > 0 && (
              <ImageList cols={4} rowHeight={100} sx={{ mb: 2 }}>
                {review.images.map((image, index) => (
                  <ImageListItem key={index}>
                    <img src={image} alt={`Review ${index + 1}`} loading="lazy" />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
            
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  startIcon={<ThumbUp />}
                  onClick={() => handleHelpful(review.id, true)}
                  disabled={!user}
                >
                  Helpful ({review.helpfulCount || 0})
                </Button>
                <Button
                  size="small"
                  startIcon={<ThumbDown />}
                  onClick={() => handleHelpful(review.id, false)}
                  disabled={!user}
                >
                  Not Helpful ({review.notHelpfulCount || 0})
                </Button>
              </Box>
              
              {review.wouldRecommend && (
                <Chip
                  label="Recommends"
                  color="success"
                  size="small"
                  icon={<CheckCircle />}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch({ type: 'reviews/clearError' })}>
          {error}
        </Alert>
      )}
      
      {renderReviewStats()}
      
      <Card>
        <CardHeader
          title={
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                Reviews ({pagination.totalElements || 0})
              </Typography>
              <Box display="flex" gap={1}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={`${filters.sortBy}-${filters.sortDir}`}
                    label="Sort By"
                    onChange={(e) => {
                      const [sortBy, sortDir] = e.target.value.split('-');
                      handleSortChange(sortBy, sortDir);
                    }}
                  >
                    <MenuItem value="createdAt-desc">Newest First</MenuItem>
                    <MenuItem value="createdAt-asc">Oldest First</MenuItem>
                    <MenuItem value="rating-desc">Highest Rating</MenuItem>
                    <MenuItem value="rating-asc">Lowest Rating</MenuItem>
                    <MenuItem value="helpfulCount-desc">Most Helpful</MenuItem>
                  </Select>
                </FormControl>
                
                {user && user.role === 'CUSTOMER' && productId && purchaseCheckDone && (
                  canUserReview() ? (
                    <Button
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={() => setReviewDialogOpen(true)}
                    >
                      Write Review
                    </Button>
                  ) : (
                    <Tooltip title="Purchase this product to leave a review">
                      <Chip
                        icon={<ShoppingBag fontSize="small" />}
                        label="Purchase to Review"
                        color="warning"
                        variant="outlined"
                      />
                    </Tooltip>
                  )
                )}
              </Box>
            </Box>
          }
        />
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : currentReviews.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No reviews yet
              </Typography>
              {user && user.role === 'CUSTOMER' && productId && purchaseCheckDone ? (
                canUserReview() ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Be the first to review this product!
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{ mt: 2 }}
                      onClick={() => setReviewDialogOpen(true)}
                    >
                      Write First Review
                    </Button>
                  </>
                ) : (
                  <Box mt={1}>
                    <ShoppingBag sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      We'd love to hear your thoughts!
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Please purchase this product to share your review — we look forward to your feedback!
                    </Typography>
                  </Box>
                )
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Be the first to review this {productId ? 'product' : 'seller'}!
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <AnimatePresence>
                {currentReviews.map(renderReviewItem)}
              </AnimatePresence>
              
              {pagination.totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.page + 1}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
      
      {renderReviewForm()}
    </Box>
  );
};

export default ReviewSystem;